import { Box, FormControl, InputAdornment, MenuItem, Select, TextField } from "@mui/material"
import type { SelectChangeEvent } from "@mui/material/Select/SelectInput"
import { FilterAltOutlined, KeyboardArrowDownOutlined, SearchOutlined } from "@mui-symbols-material/w300"
import { useNavigate, useSearch } from "@tanstack/react-router"
import type { ChangeEvent, ReactElement, ReactNode } from "react"
import { memo, useMemo } from "react"

import { Button } from "@/components/common/Button"
import { PurchaseOrderSearchCategory } from "@/screens/Procurement/types/enums"
import { getRefinedSearchParams } from "@/utils/getRefinedSearchParams"

interface PurchaseOrdersSearchBarProps {
  onShowFilter: () => void
}

const SelectIcon = memo(({ className }: { className: string }) => (
  <KeyboardArrowDownOutlined className={`text-gray-500 ${className}`} />
))

const hintMessageMap = new Map<PurchaseOrderSearchCategory, string>([
  [PurchaseOrderSearchCategory.SUPPLIER, "Search by Supplier Name or PO ID"],
  [PurchaseOrderSearchCategory.PRODUCT, "Search by Product Name or ID"],
])

export const PurchaseOrdersSearchBar = ({ onShowFilter }: PurchaseOrdersSearchBarProps): ReactElement => {
  const currentSearch = useSearch({ from: "/app/procurement/_layout/purchase-orders" })
  const navigate = useNavigate({ from: "/app/procurement/purchase-orders" })
  const { category, query, statuses } = currentSearch
  const searchHint = useMemo(() => hintMessageMap.get(category), [category])

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const queryValue = e.target.value

    const params = getRefinedSearchParams(currentSearch, {
      ...currentSearch,
      query: queryValue,
    })

    navigate(params)
  }

  const handleSearchCategoryDropdownChange = (event: SelectChangeEvent<PurchaseOrderSearchCategory>, _: ReactNode) => {
    const value = event?.target?.value as PurchaseOrderSearchCategory

    const params = getRefinedSearchParams({
      statuses,
      category: value,
    })

    navigate(params)
  }

  return (
    <Box className='flex space-x-4'>
      <Box className='flex w-full max-w-lg items-center'>
        <FormControl className='min-w-32'>
          <Select<PurchaseOrderSearchCategory>
            data-testid='search-category-select'
            value={category}
            IconComponent={SelectIcon}
            variant='outlined'
            sx={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
            onChange={handleSearchCategoryDropdownChange}
          >
            <MenuItem data-testid='supplier-option' value={PurchaseOrderSearchCategory.SUPPLIER}>
              Supplier
            </MenuItem>
            <MenuItem data-testid='product-option' value={PurchaseOrderSearchCategory.PRODUCT}>
              Product
            </MenuItem>
          </Select>
        </FormControl>
        <TextField
          data-testid='search-input'
          value={query}
          variant='outlined'
          placeholder={searchHint}
          className='w-full'
          sx={{ marginLeft: "-1px", "& fieldset": { borderTopLeftRadius: 0, borderBottomLeftRadius: 0 } }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchOutlined className='text-gray-600' />
                </InputAdornment>
              ),
            },
          }}
          onChange={handleInputChange}
        />
      </Box>
      <Button aria-label='filter' appearance='outlined' onClick={onShowFilter} startIcon={<FilterAltOutlined />}>
        Filter
      </Button>
    </Box>
  )
}
